import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography, Stack, Button, Checkbox, TextField, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Chip, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ReviewCard from './ReviewCard';
import FilterPopup from './FilterPopup';
import SortPopup from './SortPopup';
import AIIcon from '../icons/AIIcon';
import MultipleReplyIcon from '../icons/MultipleReplyIcon';
import SortIcon from '../icons/SortIcon';
import FilterIcon from '../icons/FilterIcon';
import GMBService from '../../services/GMBService';
import GeminiAIReviewService from '../../services/GeminiAIReviewService';
import api from '../../services/api';

import logger from '../../utils/logger';
const ContentContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f8f8',
  minHeight: 'calc(100vh - 120px)',
  display: 'flex',
  flexDirection: 'column',
  padding: '12px',
  paddingBottom: '100px',
  [theme.breakpoints.up('sm')]: {
    padding: '16px',
    paddingBottom: '90px',
    minHeight: 'calc(100vh - 140px)'
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px',
    paddingBottom: '80px',
    minHeight: 'calc(100vh - 160px)'
  }
}));

const Header = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: '12px',
  marginBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '26px'
  }
}));

const HeaderLeft = styled(Stack)(({ theme }) => ({
  gap: '8px',
  [theme.breakpoints.up('sm')]: {
    gap: '12px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '16px'
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  color: '#0b91d6',
  [theme.breakpoints.up('sm')]: {
    fontSize: '24px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '28px'
  }
}));

const RatingRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
  flexWrap: 'wrap'
}));

const RatingText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '18px',
  fontWeight: 600,
  color: '#121927',
  [theme.breakpoints.up('sm')]: {
    fontSize: '20px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '24px'
  }
}));

const ReviewCount = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#a0a0aa',
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '18px'
  }
}));

const ExportButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgba(160, 160, 170, 0.10)',
  border: '0.6px solid #a0a0aa',
  borderRadius: '6px',
  color: '#121927',
  width: '100%',
  minHeight: '40px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '8px 12px',
  gap: '8px',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.97)'
  },
  [theme.breakpoints.up('sm')]: {
    width: '140px',
    fontSize: '14px',
    minHeight: '44px',
    borderRadius: '4px',
    padding: '8px 16px'
  },
  [theme.breakpoints.up('md')]: {
    '&:hover': {
      backgroundColor: 'rgba(160, 160, 170, 0.20)'
    },
    '&:active': {
      transform: 'none'
    }
  }
}));

const SelectAllRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '10px',
  minHeight: '40px',
  marginBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    gap: '12px',
    marginBottom: '20px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '14px',
    minHeight: '44px',
    marginBottom: '26px'
  }
}));

const SelectAllText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#000000',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px'
  }
}));

const ActionButtonsContainer = styled(Stack)(({ theme }) => ({
  gap: '12px',
  marginBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    gap: '16px',
    marginBottom: '20px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '26px',
    marginBottom: '26px'
  }
}));

const ActionRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: '10px',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '12px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '18px',
    flexWrap: 'nowrap'
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  border: '0.6px solid #a0a0aa',
  borderRadius: '8px',
  color: '#0b91d6',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  width: '100%',
  minHeight: '40px',
  textTransform: 'none',
  padding: '8px 12px',
  gap: '6px',
  backgroundColor: 'transparent',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.97)',
    backgroundColor: 'rgba(11, 145, 214, 0.08)'
  },
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    minWidth: '120px',
    fontSize: '13px',
    padding: '8px 14px',
    gap: '8px'
  },
  [theme.breakpoints.up('md')]: {
    width: '140px',
    minHeight: '44px',
    fontSize: '14px',
    padding: '8px 16px',
    gap: '10px',
    '&:hover': {
      backgroundColor: 'rgba(11, 145, 214, 0.04)'
    },
    '&:active': {
      transform: 'none'
    }
  }
}));

const AIButton = styled(ActionButton)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  border: 'none',
  color: '#ffffff',
  fontWeight: 600,
  boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:active': {
    transform: 'scale(0.97)',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)'
  },
  '& .ai-icon': {
    filter: 'brightness(0) invert(1)',
    transition: 'all 0.3s ease'
  },
  [theme.breakpoints.up('md')]: {
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    '&:hover': {
      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
      transform: 'translateY(-2px)'
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 10px rgba(102, 126, 234, 0.4)'
    },
    '&:hover .ai-icon': {
      transform: 'scale(1.1)'
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      transition: 'left 0.5s'
    },
    '&:hover::before': {
      left: '100%'
    }
  }
}));

const FilterButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0b91d6',
  borderRadius: '8px',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  width: '100%',
  minHeight: '40px',
  textTransform: 'none',
  padding: '8px 12px',
  gap: '6px',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.97)',
    backgroundColor: '#0875a8'
  },
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    minWidth: '100px',
    fontSize: '13px',
    padding: '8px 14px',
    gap: '8px'
  },
  [theme.breakpoints.up('md')]: {
    width: '140px',
    minHeight: '44px',
    fontSize: '14px',
    padding: '8px 16px',
    gap: '10px',
    '&:hover': {
      backgroundColor: '#0980c2'
    },
    '&:active': {
      transform: 'none'
    }
  }
}));

const ReviewsContainer = styled(Box)(({ theme }) => ({
  border: '0.6px solid #f6f0f0',
  borderRadius: '10px',
  padding: '12px',
  backgroundColor: '#ffffff',
  [theme.breakpoints.up('sm')]: {
    borderRadius: '12px',
    padding: '16px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px'
  }
}));

const CreatePostButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#4caf50',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  width: '100%',
  minHeight: '40px',
  textTransform: 'capitalize',
  borderRadius: '8px',
  padding: '10px 16px',
  alignSelf: 'center',
  marginTop: '16px',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.97)',
    backgroundColor: '#3d9142'
  },
  [theme.breakpoints.up('sm')]: {
    width: '140px',
    fontSize: '14px',
    marginTop: '20px',
    marginRight: '16px'
  },
  [theme.breakpoints.up('md')]: {
    minHeight: '44px',
    borderRadius: '6px',
    marginTop: '24px',
    marginRight: '24px',
    '&:hover': {
      backgroundColor: '#45a049'
    },
    '&:active': {
      transform: 'none'
    }
  }
}));

const ReviewsManagement = () => {
  const navigate = useNavigate();
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [sortAnchor, setSortAnchor] = useState(null);
  const [filters, setFilters] = useState({
    duration: 'All time',
    rating: '1',
    response: 'All'
  });
  const [sortBy, setSortBy] = useState('new');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(0);
  const pageSize = 5;
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkReply, setBulkReply] = useState('');
  const [bulkSending, setBulkSending] = useState(false);

  // New state for AI reply dialog
  const [aiReplyOpen, setAiReplyOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState('Immediately');
  const [selectedRatings, setSelectedRatings] = useState(['5', '4', '3']);
  const [aiReplySending, setAiReplySending] = useState(false);

  // Auto-reply state
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyStatus, setAutoReplyStatus] = useState('');
  const autoReplyIntervalRef = useRef(null);
  const processedReviewsRef = useRef(new Set());

  // Fetch real reviews using selected account/location from Reputation page (stored in localStorage)
  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError('');
      const account = JSON.parse(localStorage.getItem('selectedGMBAccount') || 'null');
      const location = JSON.parse(localStorage.getItem('selectedGMBLocation') || 'null');
      if (!account?.name || !location?.name) {
        setReviews([]);
        setLoading(false);
        return;
      }
      const apiReviews = await GMBService.getReviews(undefined, account.name, location.name);
      const apiArray = Array.isArray(apiReviews) ? apiReviews : (apiReviews?.reviews || []);
      // Map to ReviewCard shape
      const toNumeric = (star) => {
        if (typeof star === 'number') return star;
        const map = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
        return map[String(star || '').toUpperCase()] || 0;
      };
      const mapped = apiArray.map((r, idx) => ({
        id: r.name || idx,
        author: r.reviewer?.displayName || 'Anonymous',
        date: r.createTime ? new Date(r.createTime).toLocaleDateString() : 'Recent',
        createdAt: r.createTime ? new Date(r.createTime).getTime() : Date.now(),
        rating: toNumeric(r.starRating),
        platform: 'Google',
        text: r.comment || 'No comment provided',
        avatar: r.reviewer?.profilePhotoUrl || '/images/karen-avatar.jpg',
        reply: r.reviewReply?.comment || null
      }));
      setReviews(mapped);
    } catch (e) {
      logger.error('Failed to load reviews:', e);
      setError('Failed to load reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Reset to first page whenever reviews list updates
  useEffect(() => {
    setPage(0);
    setSelectedReviews([]);
  }, [reviews]);

  // Reset to first page when filters/sort change
  useEffect(() => {
    setPage(0);
    setSelectedReviews([]);
  }, [filters, sortBy]);

  // Reset selection when page changes
  useEffect(() => {
    setSelectAll(false);
    setSelectedReviews([]);
  }, [page]);

  // Load auto-reply settings from backend on mount
  useEffect(() => {
    const loadAutoReplySettings = async () => {
      try {
        const location = JSON.parse(localStorage.getItem('selectedGMBLocation') || 'null');
        const locationId = location?.name?.split('/').pop() || location?.id;

        if (locationId) {
          const response = await api.get(`/customer/auto-reply/settings/${locationId}`);
          if (response?.data) {
            const settings = response.data;
            setSelectedTime(settings.trigger_time || 'Immediately');
            setSelectedRatings(settings.ratings || ['5', '4', '3']);
            if (settings.enabled) {
              setAutoReplyEnabled(true);
              setAutoReplyStatus('Auto-reply is active (runs every 5 mins on server)');
            }
          }
        } else {
          // Fallback to localStorage
          const savedSettings = localStorage.getItem('autoReplySettings');
          if (savedSettings) {
            const settings = JSON.parse(savedSettings);
            setSelectedTime(settings.triggerTime || 'Immediately');
            setSelectedRatings(settings.ratings || ['5', '4', '3']);
            if (settings.enabled) {
              setAutoReplyEnabled(true);
              setAutoReplyStatus('Auto-reply is active');
            }
          }
        }
      } catch (e) {
        logger.error('Failed to load auto-reply settings:', e);
        // Fallback to localStorage
        try {
          const savedSettings = localStorage.getItem('autoReplySettings');
          if (savedSettings) {
            const settings = JSON.parse(savedSettings);
            setSelectedTime(settings.triggerTime || 'Immediately');
            setSelectedRatings(settings.ratings || ['5', '4', '3']);
          }
        } catch (e2) {
          // Ignore
        }
      }
    };

    loadAutoReplySettings();
  }, []);

  // Convert trigger time to milliseconds
  const getTriggerTimeMs = useCallback((timeStr) => {
    switch (timeStr) {
      case 'Immediately': return 0;
      case 'After 1 hour': return 60 * 60 * 1000;
      case 'After 4 hours': return 4 * 60 * 60 * 1000;
      case 'After 24 hours': return 24 * 60 * 60 * 1000;
      default: return 0;
    }
  }, []);

  // Process auto-replies for eligible reviews
  const processAutoReplies = useCallback(async () => {
    if (!autoReplyEnabled) return;

    try {
      const settings = JSON.parse(localStorage.getItem('autoReplySettings') || '{}');
      if (!settings.enabled) return;

      const triggerMs = getTriggerTimeMs(settings.triggerTime);
      const ratingFilter = (settings.ratings || []).map(r => Number(r));
      const now = Date.now();

      // Find unreplied reviews that match criteria
      const eligibleReviews = reviews.filter(review => {
        // Skip if already has reply
        if (review.reply) return false;
        // Skip if already processed
        if (processedReviewsRef.current.has(review.id)) return false;
        // Check rating matches
        if (!ratingFilter.includes(Number(review.rating))) return false;
        // Check if review is old enough based on trigger time
        const reviewAge = now - (review.createdAt || now);
        if (reviewAge < triggerMs) return false;
        return true;
      });

      if (eligibleReviews.length === 0) return;

      setAutoReplyStatus(`Processing ${eligibleReviews.length} review(s)...`);

      for (const review of eligibleReviews) {
        try {
          // Mark as being processed
          processedReviewsRef.current.add(review.id);

          // Generate AI reply
          const businessInfo = {
            name: localStorage.getItem('businessName') || 'Our Business',
            category: localStorage.getItem('businessCategory') || 'business'
          };

          const aiReply = await GeminiAIReviewService.generateReviewReply(review, businessInfo, 'professional');

          if (aiReply) {
            // Post the reply to Google
            const account = JSON.parse(localStorage.getItem('selectedGMBAccount') || 'null');
            const location = JSON.parse(localStorage.getItem('selectedGMBLocation') || 'null');

            if (account?.name && location?.name) {
              await GMBService.replyToReview(undefined, account.name, location.name, review.id, aiReply);
              logger.log(`✅ Auto-replied to review from ${review.author}`);
              setAutoReplyStatus(`Replied to ${review.author}'s review`);
            }
          }

          // Small delay between replies to avoid rate limits
          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (err) {
          logger.error(`Failed to auto-reply to review ${review.id}:`, err);
        }
      }

      // Refresh reviews to show new replies
      await fetchReviews();
      setAutoReplyStatus('Auto-reply is active');
    } catch (error) {
      logger.error('Auto-reply processing error:', error);
      setAutoReplyStatus('Error processing auto-replies');
    }
  }, [autoReplyEnabled, reviews, getTriggerTimeMs]);

  // Auto-reply interval effect
  useEffect(() => {
    if (autoReplyEnabled) {
      // Run immediately on enable
      processAutoReplies();

      // Then run every 2 minutes to check for new reviews
      autoReplyIntervalRef.current = setInterval(() => {
        fetchReviews().then(() => processAutoReplies());
      }, 2 * 60 * 1000);

      return () => {
        if (autoReplyIntervalRef.current) {
          clearInterval(autoReplyIntervalRef.current);
        }
      };
    } else {
      if (autoReplyIntervalRef.current) {
        clearInterval(autoReplyIntervalRef.current);
      }
    }
  }, [autoReplyEnabled, processAutoReplies]);

  // Apply filters
  const now = Date.now();
  const durationToMs = (label) => {
    switch (label) {
      case 'Last 7 days': return 7 * 24 * 60 * 60 * 1000;
      case 'Last 30 days': return 30 * 24 * 60 * 60 * 1000;
      case 'Last 90 days': return 90 * 24 * 60 * 60 * 1000;
      case 'Last year': return 365 * 24 * 60 * 60 * 1000;
      default: return null; // All time
    }
  };

  const ratingToSentiment = (rating) => {
    if (rating >= 4) return 'Positive';
    if (rating <= 2) return 'Negative';
    return 'Neutral';
  };

  const filtered = reviews.filter(r => {
    // duration filter
    const dur = durationToMs(filters.duration);
    if (dur) {
      const cutoff = now - dur;
      if ((r.createdAt || now) < cutoff) return false;
    }
    // rating filter (treat as minimum rating)
    const minRating = Number(filters.rating || '1');
    if ((Number(r.rating) || 0) < minRating) return false;
    // response/sentiment filter
    if (filters.response && filters.response !== 'All') {
      if (ratingToSentiment(Number(r.rating) || 0) !== filters.response) return false;
    }
    return true;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'old':
        return (a.createdAt || 0) - (b.createdAt || 0);
      case 'high-rating':
        return (Number(b.rating) || 0) - (Number(a.rating) || 0);
      case 'low-rating':
        return (Number(a.rating) || 0) - (Number(b.rating) || 0);
      case 'new':
      default:
        return (b.createdAt || 0) - (a.createdAt || 0);
    }
  });

  const avgRating = sorted.length
    ? (sorted.reduce((s, r) => s + (Number(r.rating) || 0), 0) / sorted.length)
    : 0;

  const startIndex = page * pageSize;
  const endIndex = startIndex + pageSize;
  const pageReviews = sorted.slice(startIndex, endIndex);

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedReviews(pageReviews.map(r => r.id));
    } else {
      setSelectedReviews([]);
    }
  };

  const handleReviewSelect = (reviewId, checked) => {
    if (checked) {
      setSelectedReviews([...selectedReviews, reviewId]);
    } else {
      setSelectedReviews(selectedReviews.filter(id => id !== reviewId));
      setSelectAll(false);
    }
  };

  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
    setFilterOpen(true);
  };

  const handleSortClick = (event) => {
    setSortAnchor(event.currentTarget);
    setSortOpen(true);
  };

  const handleCreatePost = (review) => {
    // Store the selected review in localStorage so PostGenerationResults can use it
    logger.log('[ReviewsManagement] Storing review for post creation:', review);
    localStorage.setItem('selectedReviewForPost', JSON.stringify(review));
    navigate('/post-generation-results');
  };

  const handleAIReply = () => {
    setAiReplyOpen(true);
  };

  const handleMultipleReplyClick = () => {
    if (!selectedReviews || selectedReviews.length === 0) {
      alert('Please select at least one review to reply to.');
      return;
    }
    setBulkReply('');
    setBulkOpen(true);
  };

  const handleBulkSend = async () => {
    if (!bulkReply.trim()) {
      alert('Please enter a reply message.');
      return;
    }
    try {
      setBulkSending(true);
      // TODO: Integrate API to post replies per selected review ID
      logger.log('Bulk replying to reviews:', selectedReviews, 'with message:', bulkReply);
      // Simulate success
      setBulkOpen(false);
      setBulkReply('');
      setSelectedReviews([]);
      setSelectAll(false);
    } finally {
      setBulkSending(false);
    }
  };

  const handleFilterClose = () => {
    setFilterOpen(false);
    setFilterAnchor(null);
  };

  const handleSortClose = () => {
    setSortOpen(false);
    setSortAnchor(null);
  };

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    handleFilterClose();
  };

  const handleSortApply = (newSort) => {
    setSortBy(newSort);
    handleSortClose();
  };

  const handleAiReplySave = async () => {
    try {
      setAiReplySending(true);

      const account = JSON.parse(localStorage.getItem('selectedGMBAccount') || 'null');
      const location = JSON.parse(localStorage.getItem('selectedGMBLocation') || 'null');

      if (!account?.name || !location?.name) {
        throw new Error('Please select a GMB account and location first');
      }

      // Save auto-reply settings to backend
      const response = await api.post('/customer/auto-reply/settings', {
        accountName: account.name,
        locationName: location.name,
        locationId: location.name?.split('/').pop() || location.id,
        enabled: true,
        triggerTime: selectedTime,
        ratings: selectedRatings,
        tone: 'professional',
        businessName: localStorage.getItem('businessName') || account.accountName,
        businessCategory: localStorage.getItem('businessCategory') || 'business'
      });

      if (response?.error === null || response?.data) {
        logger.log('AI Auto-Reply settings saved to backend:', response);

        // Also save to localStorage for UI state
        localStorage.setItem('autoReplySettings', JSON.stringify({
          enabled: true,
          triggerTime: selectedTime,
          ratings: selectedRatings
        }));

        setAutoReplyEnabled(true);
        setAutoReplyStatus('Auto-reply is active (runs every 5 mins on server)');
        setAiReplyOpen(false);
      } else {
        throw new Error(response?.message || 'Failed to save settings');
      }
    } catch (error) {
      logger.error('Error setting AI reply:', error);
      setAutoReplyStatus('Failed to enable auto-reply: ' + error.message);
    } finally {
      setAiReplySending(false);
    }
  };

  const handleDisableAutoReply = async () => {
    try {
      const location = JSON.parse(localStorage.getItem('selectedGMBLocation') || 'null');
      const locationId = location?.name?.split('/').pop() || location?.id;

      if (locationId) {
        await api.patch(`/customer/auto-reply/settings/${locationId}/toggle`, {
          enabled: false
        });
      }

      const settings = JSON.parse(localStorage.getItem('autoReplySettings') || '{}');
      settings.enabled = false;
      localStorage.setItem('autoReplySettings', JSON.stringify(settings));

      setAutoReplyEnabled(false);
      setAutoReplyStatus('');

      if (autoReplyIntervalRef.current) {
        clearInterval(autoReplyIntervalRef.current);
      }
    } catch (error) {
      logger.error('Error disabling auto-reply:', error);
    }
  };

  const handleRatingToggle = (rating) => {
    setSelectedRatings(prev =>
      prev.includes(rating)
        ? prev.filter(r => r !== rating)
        : [...prev, rating]
    );
  };

  return (
    <ContentContainer>
      <Header>
        <HeaderLeft>
          <Title>My Reviews</Title>
          <RatingRow>
            <RatingText>{avgRating ? avgRating.toFixed(2) : '--'}</RatingText>
            <Box sx={{ width: '116px', height: '24px', ml: 1 }} />
            <ReviewCount sx={{ ml: 2 }}>{sorted.length} reviews</ReviewCount>
          </RatingRow>
        </HeaderLeft>


      </Header>

      <SelectAllRow>
        <Checkbox
          checked={selectAll}
          onChange={(e) => handleSelectAll(e.target.checked)}
          sx={{
            border: '1px solid #a0a0aa',
            borderRadius: '2px',
            width: 24,
            height: 24,
            padding: 0
          }}
        />
        <SelectAllText>Select all ({pageReviews.length})</SelectAllText>
      </SelectAllRow>

      {/* Auto-Reply Status Banner */}
      {autoReplyEnabled && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: '8px',
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleDisableAutoReply}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Disable
            </Button>
          }
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Auto-Reply Active
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {autoReplyStatus} | Trigger: {selectedTime} | Ratings: {selectedRatings.join(', ')} stars
            </Typography>
          </Box>
        </Alert>
      )}

      <ActionButtonsContainer>
        <ActionRow sx={{
          justifyContent: { xs: 'stretch', sm: 'space-between' },
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: '10px', sm: '12px', md: '18px' }} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <AIButton onClick={handleAIReply}>
              <AIIcon width={14} height={13} className="ai-icon" />
              {autoReplyEnabled ? 'Auto-Reply Settings' : 'Reply with AI'}
            </AIButton>
            <ActionButton onClick={handleMultipleReplyClick} disabled={selectedReviews.length === 0}>
              <MultipleReplyIcon width={14} height={12} color="#a0a0aa" />
              Multiple Reply {selectedReviews.length > 0 ? `(${selectedReviews.length})` : ''}
            </ActionButton>
          </Stack>
          <Stack direction="row" gap={{ xs: '10px', sm: '12px', md: '18px' }} sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 } }}>
            <ActionButton onClick={handleSortClick} sx={{ flex: { xs: 1, sm: 'none' } }}>
              <SortIcon width={16} height={14} color="#a0a0aa" />
              Sort
            </ActionButton>
            <FilterButton onClick={handleFilterClick} sx={{ flex: { xs: 1, sm: 'none' } }}>
              <FilterIcon width={16} height={14} color="#ffffff" />
              Filter
            </FilterButton>
          </Stack>
        </ActionRow>
      </ActionButtonsContainer>

      <ReviewsContainer>
        <Stack gap={{ xs: '16px', sm: '20px', md: '26px' }}>
          {pageReviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              selected={selectedReviews.includes(review.id)}
              onSelect={(checked) => handleReviewSelect(review.id, checked)}
              onAIReply={() => setAiReplyOpen(true)}
              onCreatePost={handleCreatePost}
              onReplyUpdate={fetchReviews}
            />
          ))}
        </Stack>
      </ReviewsContainer>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent={{ xs: 'center', sm: 'flex-start' }}
        alignItems="center"
        gap={{ xs: 1.5, sm: 2 }}
        sx={{ mt: { xs: 2, md: 3 }, px: { xs: 1, sm: 0 } }}
      >
        <Button
          variant="contained"
          fullWidth
          sx={{
            textTransform: 'none',
            backgroundColor: '#0b91d6',
            width: { xs: '100%', sm: '120px', md: '140px' },
            minHeight: { xs: '40px', md: '44px' },
            fontSize: { xs: '13px', md: '14px' },
            borderRadius: '8px',
            WebkitTapHighlightColor: 'transparent',
            '&:active': { transform: 'scale(0.97)' }
          }}
          onClick={() => setPage(p => Math.max(0, p - 1))}
          disabled={page === 0}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          fullWidth
          sx={{
            textTransform: 'none',
            backgroundColor: '#0b91d6',
            width: { xs: '100%', sm: '120px', md: '140px' },
            minHeight: { xs: '40px', md: '44px' },
            fontSize: { xs: '13px', md: '14px' },
            borderRadius: '8px',
            WebkitTapHighlightColor: 'transparent',
            '&:active': { transform: 'scale(0.97)' }
          }}
          onClick={() => setPage(p => p + 1)}
          disabled={endIndex >= sorted.length}
        >
          Next
        </Button>
      </Stack>


      <FilterPopup
        open={filterOpen}
        anchorEl={filterAnchor}
        onClose={handleFilterClose}
        filters={filters}
        onApply={handleFilterApply}
      />

      <SortPopup
        open={sortOpen}
        anchorEl={sortAnchor}
        onClose={handleSortClose}
        sortBy={sortBy}
        onApply={handleSortApply}
      />

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reply to {selectedReviews.length} selected {selectedReviews.length === 1 ? 'review' : 'reviews'}</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            minRows={4}
            fullWidth
            placeholder="Type your reply..."
            value={bulkReply}
            onChange={(e) => setBulkReply(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={bulkSending}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkSend} disabled={bulkSending} sx={{ backgroundColor: '#0b91d6' }}>
            {bulkSending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={aiReplyOpen} onClose={() => setAiReplyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '20px',
          fontWeight: 600,
          color: '#0B91D6',
          borderBottom: '1px solid #e5e7eb',
          pb: 2
        }}>
          Reply with AI
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            {/* Trigger Time Selection */}
            <FormControl fullWidth>
              <InputLabel sx={{ fontFamily: 'Inter, sans-serif' }}>Trigger Time</InputLabel>
              <Select
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                label="Trigger Time"
                sx={{ fontFamily: 'Inter, sans-serif' }}
              >
                <MenuItem value="Immediately">Immediately</MenuItem>
                <MenuItem value="After 1 hour">After 1 hour</MenuItem>
                <MenuItem value="After 4 hours">After 4 hours</MenuItem>
                <MenuItem value="After 24 hours">After 24 hours</MenuItem>
              </Select>
            </FormControl>

            {/* Rating Selection */}
            <Box>
              <Typography variant="body2" sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                mb: 2,
                color: '#374151'
              }}>
                Select Ratings to Reply To
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {['5', '4', '3', '2', '1'].map((rating) => (
                  <Chip
                    key={rating}
                    label={`${rating} Star${rating !== '1' ? 's' : ''}`}
                    onClick={() => handleRatingToggle(rating)}
                    color={selectedRatings.includes(rating) ? 'primary' : 'default'}
                    variant={selectedRatings.includes(rating) ? 'filled' : 'outlined'}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: selectedRatings.includes(rating)
                          ? 'rgba(11, 145, 214, 0.8)'
                          : 'rgba(11, 145, 214, 0.1)'
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Info Text */}
            <Box sx={{
              backgroundColor: '#f3f4f6',
              p: 2,
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <Typography variant="body2" sx={{
                fontFamily: 'Inter, sans-serif',
                color: '#6b7280',
                fontSize: '13px',
                lineHeight: '18px'
              }}>
                <strong>Note:</strong> AI will automatically generate appropriate replies based on the review content and rating.
                Replies will be sent to Google reviews only. No manual reply text or keywords needed.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setAiReplyOpen(false)}
            sx={{
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
              color: '#6b7280'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAiReplySave}
            variant="contained"
            disabled={aiReplySending || selectedRatings.length === 0}
            sx={{
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
              backgroundColor: '#0B91D6',
              '&:hover': { backgroundColor: '#0980c2' },
              '&:disabled': { backgroundColor: '#d1d5db' }
            }}
          >
            {aiReplySending ? 'Setting...' : 'Set AI Reply'}
          </Button>
        </DialogActions>
      </Dialog>
    </ContentContainer>
  );
};

export default ReviewsManagement;